// Module Import
//---------------------------------
import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import ReCAPTCHA from 'react-google-recaptcha'
import ArrowIconBlack from '../../../assets/images/icon_arrowBlack.png'
import Field from '../../atoms/fields/field'
import TypographyAtom from '../../atoms/typography/typography'
import LinkAtom from '../../atoms/links/links'
import ValidationWrapper from '../../atoms/fields/ValidationWrapper'
import { ROUTES } from '../../../constants/routes'
import { brandIvory, brandOrange } from '../../../assets/consts/color'
import ButtonAtom from '../../atoms/buttons/buttons'

// Component Import
//---------------------------------
const Form = styled.form`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;
  gap: 8px;

  h3 {
    margin: 0;
    color: ${brandOrange};
  }
`

const BodyWrapper = styled.div`
  display: flex;
  padding: 48px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 24px;
  align-self: stretch;

  @media only screen and (min-width: 48em) {
    padding-left: 48px;
    padding-right: 48px;
  }

  a {
    text-decoration: none;
  }

  h4 {
    font-size: 28px;
  }

  & > p {
    font-size: 20px;
  }
`

const ConfirmationWrapper = styled(BodyWrapper)`
  gap: 24px;
  padding: 24px;
  p:first-of-type {
    font-size: 24px;

    @media only screen and (min-width: 48em) {
      font-size: 28px;
    }
  }

  p,
  a {
    font-size: 16px;
  }

  @media only screen and (min-width: 48em) {
    padding-left: 48px;
    padding-right: 48px;

    p,
    a {
      font-size: 20px;
    }
  }

  p {
    margin: 0;
  }

  a {
    &:visited {
      color: ${brandOrange};
    }
  }

  button {
    margin-top: 32px;
  }
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  h5 {
    margin: 0 0 8px 0;
    font-size: 20px;
  }
`

const FieldSupport = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`

const FieldSet = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0 0 44px 0;

  input {
    margin-bottom: 16px;
  }

  input[type='file'] {
    display: none;
  }

  &:last-of-type {
    margin: 0;

    input {
      margin-bottom: 0;
    }
  }

  & input[type='file'] + label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    visibility: visible;
    height: auto;
    padding: 32px 16px;
    margin-bottom: 8px;

    background-color: ${brandIvory};
    cursor: pointer;
    transition:
      filter 0.25s ease-in-out,
      color 0.25s ease-in-out;

    &:hover {
      filter: brightness(0.8);
    }

    &:after {
      background-image: url(${ArrowIconBlack});
      background-position: center;
      background-size: 100%;
      background-repeat: no-repeat;
      content: '';
      height: 24px;
      width: 24px;
    }
  }
`

// Styled Component Declarations
//---------------------------------

// Component Export
//---------------------------------
export default function FileUploadForm({
  onSubmit,
  isDrone,
  metafields,
  loading,
  setIsLoading,
  isConfirmed,
  onFinish,
  validateInput,
  fieldErrors,
}) {
  const [smallPrintConfirm, setSmallPrintConfirm] = useState(false)
  const [insuranceLabel, setInsuranceLabel] = useState('Upload Document')
  // DON'T REMOVE
  // const [certificationLabel, setCertificationLabel] = useState('Upload Document');
  const recaptcha = useRef(null)

  // DON'T REMOVE
  // const trainingTitle = metafields.find((metafield) => metafield.key === 'training_title')?.value
  //     || 'Proof of training and other supporting documents';
  // DON'T REMOVE
  // const trainingText = metafields.find((metafield) => metafield.key === 'training_text')?.value
  //     || 'Proof of PfCO, A2 CofC or GVC qualification where required - Drag or hold shift to select multiple files.';

  const handleContinueClick = () => {
    onFinish()
  }

  const handleErrors = async ({ target: { name, value } }) => {
    await validateInput({ name, value })
  }

  const handleChangeInsuranceLabel = async ({ target }) => {
    if (target.files[0]) {
      const { name, files } = target

      setInsuranceLabel(files[0].name)
      await validateInput({ name, value: files[0] })
    }
  }

  // DON'T REMOVE
  // const handleChangeCertificationLabel = (e) => {
  //   if (e.target.files[0]) setCertificationLabel(e.target.files[0].name);
  // };

  const handleSubmit = async (event) => {
    event.preventDefault()

    const formData = new FormData(event.target)
    formData.append('smallPrintConfirm', smallPrintConfirm)

    if(isDrone){
      formData.append('isDrone', "true");
    }

    onSubmit(event, formData, recaptcha.current.getValue())
  }

  const handleCheckboxChange = async ({ target }) => {
    const { name, checked } = target
    setSmallPrintConfirm(checked)
    await validateInput({ name, value: checked })
  }

  return isConfirmed ? (
    <ConfirmationWrapper>
      <TypographyAtom
        type="P"
        copy="Thank you, please proceed to checkout to complete your booking!"
        weight="Bold"
      />
      <p>
        If you require additional help you can{' '}
        <LinkAtom linkType="Text Body" linkDestination={ROUTES.CONTACTS} linkText="contact us" />
      </p>
      <ButtonAtom
        buttonType="Submit Arrow Button"
        buttonCopy="Continue browsing"
        onClick={handleContinueClick}
      />
    </ConfirmationWrapper>
  ) : (
    <Form id="contactForm" onSubmit={handleSubmit}>
      <BodyWrapper>
        <TypographyAtom type="H4" copy="2. Upload your documents " weight="Bold" />
        <TypographyAtom
          type="P"
          copy="Please upload the required information and documentation to complete you rental"
          weight="Regular"
        />
        <FieldWrapper>
          {isDrone && (
            <>
              <TypographyAtom type="H5" copy="CAA Flyer ID" weight="Bold" />
              <FieldSet>
                <ValidationWrapper errorMessage={fieldErrors.flyerId}>
                  <Field
                      labelText="flyerId"
                      labelFor="flyerId"
                      placeholderText="Your Flyer I.D"
                      fieldType="text"
                      fieldId="flyerId"
                      fieldName="flyerId"
                      onChange={handleErrors}
                  />
                </ValidationWrapper>
                <FieldSupport>
                  <TypographyAtom
                      type="P"
                      copy=""
                      weight="Regular"
                  />
                  <LinkAtom
                      linkType="Text Body"
                      linkDestination="https://register-drones.caa.co.uk/individual/register-and-take-test-to-fly"
                      linkText="Click to register for free."
                  />
                </FieldSupport>
              </FieldSet>
              <TypographyAtom type="H5" copy="Operator ID" weight="Bold" />
              <FieldSet>
                <ValidationWrapper errorMessage={fieldErrors.operatorId}>
                  <Field
                      labelText="operatorId"
                      labelFor="operatorId"
                      placeholderText="Your Operator ID"
                      fieldType="text"
                      fieldId="operatorId"
                      fieldName="operatorId"
                      onChange={handleErrors}
                  />
                </ValidationWrapper>
                <FieldSupport>
                  <TypographyAtom
                      type="P"
                      copy="Please note an Operator ID should be affixed to the aircraft before flight"
                      weight="Regular"
                  />
                  <LinkAtom
                      linkType="Text Body"
                      linkDestination="https://register-drones.caa.co.uk/individual/register-and-take-test-to-fly"
                      linkText="Click for an Operator ID"
                  />
                </FieldSupport>
              </FieldSet>
            </>
          )}
          <TypographyAtom type="H5" copy="Proof of Insurance" weight="Bold" />
          <FieldSet>
            <ValidationWrapper errorMessage={fieldErrors.proofOfInsurance}>
              <Field
                labelText={insuranceLabel}
                labelFor="proofOfInsurance"
                placeholderText="Proof of Insurance"
                fieldType="file"
                fieldId="proofOfInsurance"
                fieldName="proofOfInsurance"
                onChange={handleChangeInsuranceLabel}
              />
            </ValidationWrapper>
            <FieldSupport>
              <TypographyAtom type="P" copy="Recommended insurer is Coverdrone" weight="Regular" />
              <LinkAtom
                linkType="Text Body"
                linkDestination="https://quotes.coverdrone.com/AWE/Container.aspx?ProductTarget=Coverdrone&CurrentStep=ClientDetails&CurrentWorkflow=StandardB2C&RefererWorkflow=StandardB2C&RefererStep=GetNewQuote&Source=28910869"
                linkText="Click to get insurance"
              />
            </FieldSupport>
          </FieldSet>
          {/* DON'T REMOVE */}
          {/* {isDrone && (
          <>
            <TypographyAtom
              type="H5"
              copy={trainingTitle}
              weight="Bold"
            />
            <FieldSet>
              <Field
                labelText={certificationLabel}
                labelFor="proofOfTraining"
                placeholderText="Proof of Training"
                fieldType="file"
                fieldId="proofOfTraining"
                fieldName="proofOfTraining"
                onChange={handleChangeCertificationLabel}
              />
              <FieldSupport>
                <TypographyAtom
                  type="P"
                  copy={trainingText}
                  weight="Regular"
                />
              </FieldSupport>
            </FieldSet>
          </>
          )} */}
          <FieldSet>
            <ValidationWrapper errorMessage={fieldErrors.smallPrintConfirm}>
              <Field
                labelText="  I confirm and accept the £99 drone rental deposit which will automatically be added to cart."
                labelFor="smallPrintConfirm"
                placeholderText="checkbox"
                fieldType="checkbox"
                fieldId="smallPrintConfirm"
                fieldName="smallPrintConfirm"
                onChange={handleCheckboxChange}
              />
            </ValidationWrapper>
          </FieldSet>
        </FieldWrapper>
        <ReCAPTCHA sitekey={process.env.GATSBY_SITE_KEY} ref={recaptcha} />
        <ButtonAtom
          buttonType="Submit Arrow Button"
          buttonCopy="Upload and Confirm Booking"
          form="contactForm"
          isLoading={loading}
          disabled={loading}
        />
      </BodyWrapper>
    </Form>
  )
}

FileUploadForm.propTypes = {}
